/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["bubbles"] }] */
import * as THREE from 'three';
import { gsap, Power1 } from 'gsap';
import _ from 'lodash';
import flashingV from '../shaders/flashing.vert';
import flashingF from '../shaders/flashing.frag';
import flashingCoordinates from '../data/flashingCoordinates.json';

class ThinkingAnimation {
    constructor(mainBrain) {
        this.mainBrain = mainBrain;
        this.isFlashing = false;
        this.memorySelected = mainBrain.memorySelected;
        this.alphaAnimation = { v: 0.0 };
        this.secuenceAnimation = 0;
        this.spontaneousTimer = 0;
        this.nextSpontaneousTime = 4.0; // First wave after 4s
        this.graphBuilt = false; // Will be built lazily on first update

        // Unified neural graph variables
        this.graphVertices = [];
        this.graphAdjacency = new Map();

        // Wave propagation simulation variables
        this.activeWaves = [];
        this.activeParticles = [];
        this.maxParticlesCount = 3000;

        // Active thinking state timer
        this.activeThinkingTimer = 0;

        // Custom delta clock to be independent of other parts
        this.clock = new THREE.Clock();
        
        // Wait for external trigger (like XRay ending) before allowing random waves
        this.isAllowedToFire = false;
    }

    enableSpontaneousPulses() {
        this.isAllowedToFire = true;
        this.nextSpontaneousTime = this.clock.getElapsedTime() + 1.0;
    }

    // Graph building logic
    buildLineGraph() {
        const memories = this.mainBrain.memories;
        const lines = memories.lines;

        this.graphVertices = [];
        this.graphAdjacency = new Map();

        const vertexMap = new Map();
        const getVertexIndex = (x, y, z) => {
            const rx = Math.round(x * 10) / 10;
            const ry = Math.round(y * 10) / 10;
            const rz = Math.round(z * 10) / 10;
            const key = `${rx},${ry},${rz}`;

            if (vertexMap.has(key)) {
                return vertexMap.get(key);
            }

            const idx = this.graphVertices.length;
            this.graphVertices.push(new THREE.Vector3(x, y, z));
            vertexMap.set(key, idx);
            return idx;
        };

        const addEdge = (idx0, idx1) => {
            if (idx0 === idx1) return;
            if (!this.graphAdjacency.has(idx0)) this.graphAdjacency.set(idx0, []);
            if (!this.graphAdjacency.has(idx1)) this.graphAdjacency.set(idx1, []);
            if (!this.graphAdjacency.get(idx0).includes(idx1)) this.graphAdjacency.get(idx0).push(idx1);
            if (!this.graphAdjacency.get(idx1).includes(idx0)) this.graphAdjacency.get(idx1).push(idx0);
        };

        let loadedLines = false;

        if (lines) {
            Object.keys(lines).forEach((groupName) => {
                const positions = lines[groupName];
                if (!positions || positions.length === 0) return;
                
                loadedLines = true;
                for (let i = 0; i < positions.length; i += 6) {
                    if (i + 5 >= positions.length) break;
                    const idx0 = getVertexIndex(positions[i], positions[i + 1], positions[i + 2]);
                    const idx1 = getVertexIndex(positions[i + 3], positions[i + 4], positions[i + 5]);
                    addEdge(idx0, idx1);
                }
            });
        }

        if (!loadedLines) {
            // console.log("[ThinkingAnimation] No lines available, building graph from mesh triangle edges.");
            const memoryKeys = Object.keys(memories).filter(k => k !== 'lines');
            memoryKeys.forEach((key) => {
                const geomArray = memories[key];
                if (geomArray && geomArray.length > 0) {
                    const geometry = geomArray[0];
                    const positions = geometry.attributes.position.array;
                    
                    let minZ = Infinity; let maxZ = -Infinity;
                    let minX = Infinity; let maxX = -Infinity;
                    for (let i = 0; i < positions.length; i += 3) {
                        if (positions[i] < minX) minX = positions[i];
                        if (positions[i] > maxX) maxX = positions[i];
                        if (positions[i+2] < minZ) minZ = positions[i+2];
                        if (positions[i+2] > maxZ) maxZ = positions[i+2];
                    }
                    // console.log(`[Diagnostic] ${key}: X(${minX.toFixed(1)} to ${maxX.toFixed(1)}), Z(${minZ.toFixed(1)} to ${maxZ.toFixed(1)})`);

                    // Note: We use steps of 9 (3 vertices * 3 coordinates) assuming non-indexed geometries
                    // (OBJ loader often produces non-indexed buffer geometries)
                    for (let i = 0; i < positions.length; i += 9) {
                        if (i + 8 >= positions.length) break;
                        const v0 = getVertexIndex(positions[i], positions[i+1], positions[i+2]);
                        const v1 = getVertexIndex(positions[i+3], positions[i+4], positions[i+5]);
                        const v2 = getVertexIndex(positions[i+6], positions[i+7], positions[i+8]);
                        
                        addEdge(v0, v1);
                        addEdge(v1, v2);
                        addEdge(v2, v0);
                    }
                }
            });
        }

        // Add cross-hemisphere bridges to prevent left-side isolation
        let bridgesAdded = 0;
        for (let i = 0; i < this.graphVertices.length; i++) {
            const v1 = this.graphVertices[i];
            if (Math.abs(v1.z) < 15) { // Near the midline
                // find closest vertex on the OTHER side of the midline
                let closestIdx = -1;
                let minDist = 25; // Max bridge distance
                for (let j = 0; j < this.graphVertices.length; j++) {
                    const v2 = this.graphVertices[j];
                    if (v1.z * v2.z < 0) { // Opposite sides
                        const dist = v1.distanceTo(v2);
                        if (dist < minDist) {
                            minDist = dist;
                            closestIdx = j;
                        }
                    }
                }
                if (closestIdx !== -1) {
                    addEdge(i, closestIdx);
                    bridgesAdded++;
                }
            }
        }
        // console.log(`[ThinkingAnimation] Added ${bridgesAdded} cross-hemisphere bridges`);

        if (this.graphVertices.length > 0) {
            this.graphBounds = new THREE.Box3().setFromPoints(this.graphVertices);
        }

        // console.log(`[ThinkingAnimation] Built unified brain graph: ${this.graphVertices.length} vertices, ${this.graphAdjacency.size} nodes with edges.`);
    }

    findClosestVertex(pos) {
        let closestIdx = -1;
        let minDist = Infinity;
        for (let i = 0; i < this.graphVertices.length; i++) {
            const v = this.graphVertices[i];
            const dist = pos.distanceTo(v);
            if (dist < minDist) {
                minDist = dist;
                closestIdx = i;
            }
        }
        return closestIdx;
    }

    // Particle manager
    spawnParticle(type, pos, color, size, lifetime, velocity = null) {
        if (this.activeParticles.length >= this.maxParticlesCount) {
            // Pool reuse: replace oldest particle that is NOT a main PULSE
            let indexToReplace = -1;
            for (let i = 0; i < this.activeParticles.length; i++) {
                if (this.activeParticles[i].type !== 'PULSE') {
                    indexToReplace = i;
                    break;
                }
            }
            if (indexToReplace === -1) indexToReplace = 0;
            
            this.activeParticles[indexToReplace] = {
                type,
                pos: pos.clone(),
                color: color.clone(),
                size,
                lifetime,
                maxLifetime: lifetime,
                seed: Math.random(),
                velocity: velocity ? velocity.clone() : null
            };
        } else {
            this.activeParticles.push({
                type,
                pos: pos.clone(),
                color: color.clone(),
                size,
                lifetime,
                maxLifetime: lifetime,
                seed: Math.random(),
                velocity: velocity ? velocity.clone() : null
            });
        }
    }

    // Wave firing
    fireWave(startVertexIdx, color, speed = 180, maxJumps = 200, onComplete = null) {
        if (!this.graphBuilt || startVertexIdx < 0 || startVertexIdx >= this.graphVertices.length) return;

        const neighbors = this.graphAdjacency.get(startVertexIdx);
        if (!neighbors || neighbors.length === 0) return;

        const fronts = [];
        const numFronts = Math.min(neighbors.length, 2);
        const selectedNeighbors = _.sampleSize(neighbors, numFronts);

        selectedNeighbors.forEach((targetIdx) => {
            fronts.push({
                currentVertex: startVertexIdx,
                targetVertex: targetIdx,
                t: 0.0,
                speed: speed * THREE.MathUtils.randFloat(0.8, 1.2),
                color: color.clone(),
                pulseTimer: 0.0
            });
        });

        const visitedNodes = new Set();
        visitedNodes.add(startVertexIdx);
        selectedNeighbors.forEach(n => visitedNodes.add(n));

        this.activeWaves.push({
            fronts: fronts,
            visitedNodes: visitedNodes,
            maxJumps: maxJumps,
            onComplete: onComplete
        });
        // console.log(`[Synapse] fireWave OK: startVertex=${startVertexIdx}, speed=${speed.toFixed(0)}, fronts=${fronts.length}, totalWaves=${this.activeWaves.length}`);
    }

    getMemoryThemeColor(key) {
        switch (key) {
            case 'episodic':
                return new THREE.Color(0x9d00ff); // Neon Violet
            case 'semantic':
                return new THREE.Color(0x00f3ff); // Electric Cyan
            case 'analytic':
                return new THREE.Color(0x0055ff); // Electric Blue
            case 'process':
                return new THREE.Color(0x00ff80); // Luminous Emerald
            case 'affective':
                return new THREE.Color(0xff007f); // Hot Pink/Magenta
            default:
                return new THREE.Color(0x00f3ff);
        }
    }

    getRandomPremiumColor() {
        const colors = [
            new THREE.Color(0x99ccff), // Dim powder blue
            new THREE.Color(0xcccccc), // Dim white
            new THREE.Color(0xaabbff), // Dim periwinkle
            new THREE.Color(0x88dddd), // Dim cyan
            new THREE.Color(0xffccff)  // Dim pink
        ];
        return _.sample(colors);
    }

    initAnimation() {
        const { scene, camera } = this.mainBrain;

        // NOTE: buildLineGraph() is called lazily on first update() call,
        // because memories.lines may still be empty at this point
        // (OBJ model loading is async and addBrain() may not be called yet).

        const particles = this.maxParticlesCount;
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particles * 3);
        const colors = new Float32Array(particles * 3);
        const sizes = new Float32Array(particles);
        const alphas = new Float32Array(particles);

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));
        geometry.setAttribute('aAlpha', new THREE.Float32BufferAttribute(alphas, 1));

        geometry.computeBoundingSphere();

        const sparkTexture = this.mainBrain.loaders.spark;
        const hasTexture = sparkTexture && sparkTexture.isTexture;
        // console.log('[ThinkingAnimation] initAnimation — spark texture available:', hasTexture, sparkTexture);

        const customMaterial = new THREE.ShaderMaterial({
            uniforms: {
                c: { type: 'f', value: 0.9 },
                p: { type: 'f', value: 2.8 },
                glowColor: { type: 'c', value: new THREE.Color(0x2c3e93) },
                viewVector: { type: 'v3', value: camera.position },
                uTime: { type: 'f', value: 0.0 },
                // uFadeTime = 1.0 always; per-particle alpha controlled via aAlpha
                uFadeTime: { type: 'f', value: 1.0 },
                uMouse: { type: 'f', value: new THREE.Vector2(0.0) },
                isCustomAlpha: { type: 'b', value: false },
                uAlpha: { type: 'float', value: 0.0 },
                uResolution: {
                    type: 'v2',
                    value: new THREE.Vector2(window.innerWidth, window.innerHeight),
                },
                uIsFlashing: { type: 'b', value: false },
                uFlashingAlpha: { type: 'f', value: 0.0 },
                uTexture: { value: hasTexture ? sparkTexture : null },
                uHasTexture: { type: 'b', value: hasTexture },
            },
            vertexShader: flashingV,
            fragmentShader: flashingF,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            depthTest: false,
            transparent: true,
        });

        this.flashing = new THREE.Points(geometry, customMaterial);
        this.flashing.name = 'flashing';
        scene.add(this.flashing);
        // console.log('[ThinkingAnimation] initAnimation complete. Points object added to scene.');

        // Reset delta clock
        this.clock.getDelta();
    }

    animationCamera(val) {
        this.mainBrain.isRecording = false;
        this.flashing.material.uniforms.uFadeTime.value = 1;
        this.isFlashing = true;

        if (this.alphaAnimation.v === 0.0) {
            gsap.fromTo(
                this.alphaAnimation,
                { v: 0.0 },
                {
                    v: 1.0,
                    duration: 2.5,
                    ease: Power1.easeInOut,
                    onStart: () => {
                        this.selectMemoryThinking(val);
                    },
                    onUpdate: () => {
                        this.flashing.material.uniforms.uFadeTime.value = this.alphaAnimation.v;
                    },
                },
            );
        } else {
            gsap.fromTo(
                this.alphaAnimation,
                { v: 1.0 },
                {
                    v: 0.0,
                    duration: 1.0,
                    ease: Power1.easeInOut,
                    onUpdate: () => {
                        this.flashing.material.uniforms.uFadeTime.value = this.alphaAnimation.v;
                    },
                    onComplete: () => {
                        this.thinkingFadeIn(val);
                    },
                },
            );
        }
    }

    selectMemoryThinking(val) {
        const lights = Object.keys(flashingCoordinates);
        const light = lights[Math.floor(val % lights.length)];

        const locations = flashingCoordinates[light];
        if (!locations) {
            return;
        }

        // Trigger waves at this memory center
        if (arguments.length <= 1 || arguments[1] !== false) {
            const startVertex = this.findClosestVertex(new THREE.Vector3(locations.x, locations.y, locations.z));
            const color = this.getMemoryThemeColor(light);
            if (startVertex !== -1) {
                this.fireWave(startVertex, color, 220);
                setTimeout(() => this.fireWave(startVertex, color, 180), 100);
                setTimeout(() => this.fireWave(startVertex, color, 250), 200);
            }
        }

        if (arguments.length > 1 && arguments[1] === false) {
            return; // Skip camera animation if second arg is false
        }

        const { camera } = this.mainBrain;

        const cameraPos = {
            x: camera.position.x,
            y: camera.position.y,
            z: camera.position.z,
        };

        gsap.fromTo(
            cameraPos,
            { x: cameraPos.x, y: cameraPos.y, z: cameraPos.z },
            {
                x: locations.camera.x,
                y: locations.camera.y,
                z: locations.camera.z,
                duration: 1.5,
                ease: Power1.easeInOut,
                onUpdate: () => {
                    camera.position.x = cameraPos.x;
                    camera.position.y = cameraPos.y;
                    camera.position.z = cameraPos.z;
                },
                onComplete: () => {
                    this.secuenceAnimation += 1;
                    this.animationCamera(this.secuenceAnimation);
                },
            },
        );
    }

    thinkingFadeIn(val) {
        gsap.fromTo(
            this.alphaAnimation,
            { v: 0.0 },
            {
                v: 1.0,
                duration: 2.5,
                ease: Power1.easeInOut,
                onUpdate: () => {
                    this.flashing.material.uniforms.uFadeTime.value = this.alphaAnimation.v;
                },
                onStart: () => {
                    this.selectMemoryThinking(val);
                },
            },
        );
    }

    updateSubSystem(subsystemPayload) {
        // Handled inside bubblesAnimation.js
    }

    update(camera, totalTime) {
        if (!this.tempViewVector) this.tempViewVector = new THREE.Vector3();
        this.tempViewVector.subVectors(camera.position, this.flashing.position);
        this.flashing.material.uniforms.viewVector.value = this.tempViewVector;

        this.flashing.material.uniforms.uTime.value = totalTime;

        // --- DIAGNOSTIC LOG every 5 seconds ---
        if (!this._lastDiagLog || totalTime - this._lastDiagLog > 5.0) {
            this._lastDiagLog = totalTime;
            // console.log(
            //     `[Synapse DIAG t=${totalTime.toFixed(1)}s]`,
            //     `graphBuilt=${this.graphBuilt}`,
            //     `vertices=${this.graphVertices.length}`,
            //     `activeWaves=${this.activeWaves.length}`,
            //     `activeParticles=${this.activeParticles.length}`,
            //     `isFlashing=${this.isFlashing}`,
            //     `uFadeTime=${this.flashing.material.uniforms.uFadeTime.value}`,
            //     `nextSpontaneous=${this.nextSpontaneousTime.toFixed(1)}s`,
            // );
            // Log memories.lines state
            const lines = this.mainBrain.memories.lines;
            const lineLengths = lines ? Object.entries(lines).map(([k,v]) => `${k}:${v ? v.length : 'null'}`) : [];
            // console.log('[Synapse DIAG] memories.lines lengths:', lineLengths.join(', '));
        }

        // Lazily build the graph after the OBJ model is fully loaded.
        if (!this.graphBuilt) {
            const memories = this.mainBrain.memories;
            // Check if either lines or main meshes are loaded
            const hasLines = memories.lines && Object.values(memories.lines).some(arr => arr && arr.length > 0);
            const hasMeshes = ['episodic', 'semantic', 'analytic', 'process', 'affective'].some(key => memories[key] && memories[key].length > 0);
            
            if (hasLines || hasMeshes) {
                this.buildLineGraph();
                this.graphBuilt = true;
            }
        }

        // Get frame delta time safely
        const dt = Math.min(this.clock.getDelta(), 0.1);

        // 1. Update Waves
        const nextWaves = [];
        this.activeWaves.forEach((wave) => {
            const nextFronts = [];

            wave.fronts.forEach((front) => {
                const p0 = this.graphVertices[front.currentVertex];
                const p1 = this.graphVertices[front.targetVertex];
                const edgeLen = p0.distanceTo(p1);

                if (edgeLen === 0) return;

                front.t += (front.speed * dt) / edgeLen;

                const currentPos = new THREE.Vector3().lerpVectors(p0, p1, Math.min(front.t, 1.0));

                front.pulseTimer += dt;
                if (front.pulseTimer >= 0.035) { // spawn a trail particle every 35ms
                    front.pulseTimer = 0;
                    this.spawnParticle('TRAIL', currentPos, front.color, 12.0, 0.5); 

                    // Occasionally spawn small sparks shooting sideways
                    if (Math.random() < 0.25) {
                        const vel = new THREE.Vector3(
                            THREE.MathUtils.randFloat(-50, 50),
                            THREE.MathUtils.randFloat(-50, 50),
                            THREE.MathUtils.randFloat(-50, 50)
                        );
                        this.spawnParticle('SPARK', currentPos, front.color, 8.0, 0.2, vel);
                    }
                }

                // Lead PULSE particle
                this.spawnParticle('PULSE', currentPos, new THREE.Color(0xffffff), 24.0, 0.05);

                    if (front.t >= 1.0) {
                        const arrivedIdx = front.targetVertex;

                        // Light up node
                        this.spawnParticle('NODE', p1, front.color, 16.0, 1.2);

                        const neighbors = this.graphAdjacency.get(arrivedIdx) || [];
                        const validNeighbors = neighbors.filter(n => !wave.visitedNodes.has(n));

                        if (validNeighbors.length > 0) {
                            let maxBranches = Math.min(validNeighbors.length, 2);
                            
                            // Prevent exponential explosion! Cap total fronts per wave.
                            if (nextFronts.length >= 8) {
                                maxBranches = 1;
                            }
                            if (nextFronts.length >= 12) {
                                maxBranches = 0;
                            }

                            const selectedNeighbors = _.sampleSize(validNeighbors, maxBranches);

                            selectedNeighbors.forEach((nextNeighbor) => {
                                wave.visitedNodes.add(nextNeighbor);

                                // Hard limit on total distance the wave can travel
                                if (wave.visitedNodes.size > wave.maxJumps) return;

                                nextFronts.push({
                                    currentVertex: arrivedIdx,
                                    targetVertex: nextNeighbor,
                                    t: 0.0,
                                    speed: front.speed,
                                    color: front.color.clone(),
                                    pulseTimer: 0.0
                                });
                            });
                        }
                    } else {
                        nextFronts.push(front);
                    }
            });

            wave.fronts = nextFronts;
            if (wave.fronts.length > 0) {
                nextWaves.push(wave);
            } else {
                if (wave.onComplete) wave.onComplete();
            }
        });
        this.activeWaves = nextWaves;

        // 2. Update Particles
        const nextParticles = [];
        this.activeParticles.forEach((p) => {
            p.lifetime -= dt;

            // Physics for sparks
            if (p.velocity) {
                p.pos.addScaledVector(p.velocity, dt);
                p.velocity.multiplyScalar(0.9);
            }

            if (p.lifetime > 0) {
                nextParticles.push(p);
            }
        });
        this.activeParticles = nextParticles;

        // 3. Write active particles to WebGL buffer geometry
        const positions = this.flashing.geometry.attributes.position.array;
        const colors = this.flashing.geometry.attributes.color.array;
        const sizes = this.flashing.geometry.attributes.size.array;
        const alphas = this.flashing.geometry.attributes.aAlpha.array;

        const count = Math.min(this.activeParticles.length, this.maxParticlesCount);

        for (let i = 0; i < this.maxParticlesCount; i++) {
            if (i < count) {
                const p = this.activeParticles[i];
                positions[i * 3 + 0] = p.pos.x;
                positions[i * 3 + 1] = p.pos.y;
                positions[i * 3 + 2] = p.pos.z;

                colors[i * 3 + 0] = p.color.r;
                colors[i * 3 + 1] = p.color.g;
                colors[i * 3 + 2] = p.color.b;

                let sizeScale = 1.0;
                if (p.type === 'TRAIL') {
                    sizeScale = p.lifetime / p.maxLifetime;
                } else if (p.type === 'NODE') {
                    sizeScale = 0.8 + 0.3 * Math.sin(p.seed * 50.0 + p.lifetime * 40.0);
                } else if (p.type === 'SPARK') {
                    sizeScale = p.lifetime / p.maxLifetime;
                }
                sizes[i] = p.size * sizeScale;

                let alpha = p.lifetime / p.maxLifetime;
                if (p.type === 'NODE') {
                    const age = p.maxLifetime - p.lifetime;
                    if (age < 0.1) {
                        alpha = age / 0.1;
                    } else {
                        alpha = p.lifetime / (p.maxLifetime - 0.1);
                    }
                    alpha *= (0.6 + 0.4 * Math.sin(p.seed * 100.0 + p.lifetime * 30.0));
                } else if (p.type === 'PULSE') {
                    alpha = 1.0;
                }
                alphas[i] = alpha;
            } else {
                positions[i * 3 + 0] = 0;
                positions[i * 3 + 1] = 0;
                positions[i * 3 + 2] = 0;
                colors[i * 3 + 0] = 0;
                colors[i * 3 + 1] = 0;
                colors[i * 3 + 2] = 0;
                sizes[i] = 0;
                alphas[i] = 0;
            }
        }

        this.flashing.geometry.attributes.position.needsUpdate = true;
        this.flashing.geometry.attributes.color.needsUpdate = true;
        this.flashing.geometry.attributes.size.needsUpdate = true;
        this.flashing.geometry.attributes.aAlpha.needsUpdate = true;

        this.flashing.geometry.setDrawRange(0, count);

        // 4. Spontaneous Synapses trigger
        if (this.isAllowedToFire && !this.isFlashing && !this.mainBrain.isRecording && this.graphBuilt) {
            if (totalTime > this.nextSpontaneousTime) {
                this.nextSpontaneousTime = totalTime + THREE.MathUtils.randFloat(3.5, 6.5);
                
                // Only fire spontaneous if no memories are currently glowing intensely
                if (this.activeWaves.length === 0) {
                    // 1. Pick a random memory region to get exactly 20% flat chance per region
                    const validKeys = ['episodic', 'semantic', 'analytic', 'process', 'affective'];
                    const randomKey = validKeys[Math.floor(Math.random() * validKeys.length)];
                    
                    const geomArray = this.mainBrain.memories[randomKey];
                    if (geomArray && geomArray.length > 0) {
                        const positions = geomArray[0].attributes.position.array;
                        // 2. Pick a random vertex inside that region's geometry
                        const randIdx = Math.floor(Math.random() * (positions.length / 3)) * 3;
                        const randomPt = new THREE.Vector3(positions[randIdx], positions[randIdx+1], positions[randIdx+2]);
                        
                        const startVertexIdx = this.findClosestVertex(randomPt);
                        
                        if (startVertexIdx !== -1) {
                            const color = this.getRandomPremiumColor();
                            const speed = THREE.MathUtils.randFloat(120, 180); // Slower
                            const maxJumps = Math.floor(THREE.MathUtils.randFloat(60, 110));
                            this.fireWave(startVertexIdx, color, speed, maxJumps);
                        }
                    }
                }
            }
        }

        // 5. Active Thinking Cascade Storm
        if (this.isFlashing) {
            this.activeThinkingTimer += dt;
            if (this.activeThinkingTimer >= 0.22) {
                this.activeThinkingTimer = 0;

                const winnerGroup = this.mainBrain.bubblesAnimation ? this.mainBrain.bubblesAnimation.winnerGroup : null;
                const memoryKey = winnerGroup || 'episodic';
                
                const geomArray = this.mainBrain.memories[memoryKey];
                if (geomArray && geomArray.length > 0) {
                    const positions = geomArray[0].attributes.position.array;
                    const randIdx = Math.floor(Math.random() * (positions.length / 3)) * 3;
                    const randomPt = new THREE.Vector3(positions[randIdx], positions[randIdx+1], positions[randIdx+2]);
                    
                    const startVertex = this.findClosestVertex(randomPt);
                    const color = this.getMemoryThemeColor(memoryKey);

                    this.fireWave(startVertex, color, THREE.MathUtils.randFloat(180, 260));
                }
            }
        }
    }

    isActive(val) {
        if (val) {
            this.isFlashing = true;
            this.activeThinkingTimer = 0;

            // Ensure uFadeTime is always 1.0 (visibility is per-particle via aAlpha)
            this.flashing.material.uniforms.uFadeTime.value = 1.0;

            const winnerGroup = this.mainBrain.bubblesAnimation ? this.mainBrain.bubblesAnimation.winnerGroup : null;
            const memoryKey = winnerGroup || 'episodic';

            if (this.graphBuilt) {
                const geomArray = this.mainBrain.memories[memoryKey];
                if (geomArray && geomArray.length > 0) {
                    const positions = geomArray[0].attributes.position.array;
                    const randIdx = Math.floor(Math.random() * (positions.length / 3)) * 3;
                    const randomPt = new THREE.Vector3(positions[randIdx], positions[randIdx+1], positions[randIdx+2]);
                    
                    const startVertex = this.findClosestVertex(randomPt);
                    const color = this.getMemoryThemeColor(memoryKey);

                    if (startVertex !== -1) {
                        this.fireWave(startVertex, color, 220);
                        setTimeout(() => this.fireWave(startVertex, color, 180), 100);
                        setTimeout(() => this.fireWave(startVertex, color, 250), 200);
                        setTimeout(() => this.fireWave(startVertex, color, 200), 300);
                    }
                }
            }
        } else {
            // Only stop firing new cascade waves; let active particles fade naturally
            this.isFlashing = false;
        }
    }

    updateMouse(coordinates) {
        this.flashing.material.uniforms.uMouse.value = coordinates;
    }

    flashingAnimation(isActive) {
        this.flashing.material.uniforms.uIsFlashing.value = isActive;
        if (isActive) {
            const progress = { p: 0.0 };
            gsap.fromTo(
                progress,
                { p: 0.0 },
                {
                    p: 1.0,
                    duration: 2.5,
                    ease: Power1.easeInOut,
                    onUpdate: () => {
                        this.flashing.material.uniforms.uFlashingAlpha.value = progress.p;
                        this.isFlashing = true;
                    },
                },
            );
        } else {
            const progress = { p: 1.0 };
            gsap.fromTo(
                progress,
                { p: 1.0 },
                {
                    p: 0.0,
                    duration: 2.5,
                    ease: Power1.easeInOut,
                    onUpdate: () => {
                        this.flashing.material.uniforms.uFlashingAlpha.value = progress.p;
                        this.isFlashing = false;
                    },
                },
            );
        }
    }

    animate(isActive) {
        const cameraPos = this.mainBrain.camera.position;
        const { target } = this.mainBrain.orbitControls;
        if (!this.isFlashing) {
            this.flashingAnimation(true);
        }
        if (isActive) {
            const progress = { p: 0.0 };
            gsap.fromTo(
                progress,
                { p: 0.0 },
                {
                    p: 1.0,
                    duration: 2.5,
                    ease: Power1.easeInOut,
                    onUpdate: () => {
                        this.mainBrain.orbitControls.target.set(
                            target.x,
                            target.y + progress.p,
                            target.z,
                        );
                        this.mainBrain.camera.position.set(
                            cameraPos.x,
                            cameraPos.y + progress.p,
                            cameraPos.z,
                        );
                    },
                },
            );
        } else {
            const progress = { p: 1.0 };
            gsap.fromTo(
                progress,
                { p: 1.0 },
                {
                    p: 0.0,
                    duration: 2.5,
                    ease: Power1.easeInOut,
                    onUpdate: () => {
                        this.mainBrain.orbitControls.target.set(
                            target.x,
                            target.y - progress.p,
                            target.z,
                        );
                        this.mainBrain.camera.position.set(
                            cameraPos.x,
                            cameraPos.y - progress.p,
                            cameraPos.z,
                        );
                    },
                },
            );
        }
    }
}

export default ThinkingAnimation;
